import { styled } from '@linaria/react';
import { Group, Switch } from '@mantine/core';
import { Background, Controls, ReactFlow, addEdge, useEdgesState, useNodesState, type Connection } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AgentEventLog } from '../../../api/events-client/events-client.type';
import { secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { TaskRecord } from '../../tasks-table/tasks-table.type';
import { getAgentGraphNodes } from './agent-graph.helpers';
import { AgentEventEdge } from './edges/agent-event-edge';
import { SessionEdge } from './edges/session-edge';
import { TaskStatusEdge } from './edges/task-status-edge';
import { DotNode, HeaderNode } from './nodes/agent-graph-nodes';

const nodeTypes = {
  dot: DotNode,
  header: HeaderNode,
};

const edgeTypes = {
  agentEvent: AgentEventEdge,
  session: SessionEdge,
  taskStatus: TaskStatusEdge,
};

type Props = {
  task?: TaskRecord | null;
  sessionId?: string | null;
  agentEvents: AgentEventLog[];
  setSelectedEvent: (event: AgentEventLog) => void;
  selectedEvent?: AgentEventLog | null;
};

export const AgentGraph = ({ task, sessionId, agentEvents, setSelectedEvent, selectedEvent }: Props) => {
  const [showTaskStatus, setShowTaskStatus] = useState(!sessionId);
  const [collapsedSessions, setCollapsedSessions] = useState<Set<string>>(() => {
    if (sessionId) {
      return new Set();
    }
    const sessionIds = new Set<string>();
    agentEvents.forEach((event) => {
      if (event.session_id) {
        sessionIds.add(event.session_id);
      }
    });
    return sessionIds;
  });

  const toggleSessionCollapse = useCallback((sessionId: string) => {
    setCollapsedSessions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  }, []);

  const collapseAllSessions = useCallback(() => {
    const allSessionIds = new Set<string>();
    agentEvents.forEach((event) => {
      if (event.session_id) {
        allSessionIds.add(event.session_id);
      }
    });
    setCollapsedSessions(allSessionIds);
  }, [agentEvents]);

  const uncollapseAllSessions = useCallback(() => {
    setCollapsedSessions(new Set());
  }, []);

  const { nodes: eventNodes, edges: eventEdges } = useMemo(
    () =>
      getAgentGraphNodes(
        agentEvents,
        collapsedSessions,
        toggleSessionCollapse,
        setSelectedEvent,
        task?.transitions || [],
        showTaskStatus,
        selectedEvent,
      ),
    [
      agentEvents,
      collapsedSessions,
      toggleSessionCollapse,
      setSelectedEvent,
      task?.transitions,
      showTaskStatus,
      selectedEvent,
    ],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(eventNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(eventEdges);

  // Update React Flow state when computed nodes/edges change
  useEffect(() => {
    setNodes(eventNodes);
  }, [eventNodes, setNodes]);

  useEffect(() => {
    setEdges(eventEdges);
  }, [eventEdges, setEdges]);

  useEffect(() => {
    setShowTaskStatus(!sessionId);
  }, [sessionId]);

  const onConnect = useCallback((params: Connection) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  return (
    <FlowContainer>
      <ControlsBar>
        <Text size="medium" weight="bold">
          {task?.title || sessionId || 'Agent Session Events'}
        </Text>
        <Group>
          {!sessionId && (
            <Switch
              checked={showTaskStatus}
              onChange={() => setShowTaskStatus(!showTaskStatus)}
              color={secondaryBase}
              label={<Text size="small">Show Task Status</Text>}
            />
          )}
          <Button
            size="xs"
            radius="xl"
            variant="outline"
            onClick={collapseAllSessions}
            leftSection={<Icon name="unfold_less" size={16} />}
          >
            Collapse All
          </Button>
          <Button
            size="xs"
            radius="xl"
            variant="outline"
            onClick={uncollapseAllSessions}
            leftSection={<Icon name="unfold_more" size={16} />}
          >
            Expand All
          </Button>
        </Group>
      </ControlsBar>
      <ReactFlowContainer>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          zoomOnScroll={false}
          panOnScroll={true}
        >
          <Controls showInteractive={false} />
          <Background />
        </ReactFlow>
      </ReactFlowContainer>
    </FlowContainer>
  );
};

const FlowContainer = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--sky-light);
  background-color: transparent;
  border-radius: 8px 8px 0 0;
`;
const ReactFlowContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;
